/**
 * ============================================================================
 * Registro central de ações do botão "Novo" (Quick Create) no header
 * ----------------------------------------------------------------------------
 * Cada entrada representa um atalho de criação. A ativação ocorre navegando
 * para a rota informada com `?new=<value>` — a página destino observa esse
 * parâmetro (via useAutoOpenFromQuery) e abre o modal correspondente.
 *
 * RELEASE:
 *   2026-08-07 v1.7.0 - Nova ação `qc_contact_note` (Anotações): abre o modal
 *     de anotação do chat sobre a tela atual, com busca de contato.
 *   2026-08-06 v1.6.0 - Campos `inline` (abre modal na tela atual, sem
 *     navegar) e `shortLabel` (rótulo curto no menu compacto do "+").
 *   2026-08-06 v1.5.0 - Campos `primary` e `shortcutKey`: definem a lista
 *     curta (Contato, Cliente, Negócio, Tarefa, Evento, Iniciar conversa)
 *     exibida no menu compacto do botão "+" ao lado da busca global.
 *   2026-07-24 v1.4.0 - Blindagem de permissões. Adiciona
 *     `requireAdmin?: boolean` na interface e o campo é respeitado por
 *     useAvailableQuickCreates. Preenche `menuKeys` faltantes para os
 *     atalhos cujas rotas destino já são protegidas por PermissionGuard:
 *       - qc_start_chat -> ['chat']
 *       - qc_contact    -> ['contacts']
 *       - qc_booking    -> ['scheduling']
 *       - qc_affiliate  -> ['referral_affiliates']
 *       - qc_agent      -> ['agents']
 *     E marca qc_company como requireAdmin (rota /tenant/company).
 *     qc_template permanece sem menuKeys (rota Templates é aberta a todos).
 *   2026-07-24 v1.3.0 - Substitui atalho único "Nova lista de importação"
 *     por 4 atalhos específicos (Contatos/Negócios/Clientes/Afiliados).
 *   2026-07-24 v1.2.0 - Novos atalhos: Nova ação rápida (routing), Novo
 *     fluxo, Novo departamento, Nova lista, Novo programa/campanha
 *     de indicação.
 *   2026-07-24 v1.1.0 - Iniciar conversa/Novo contrato/Nova tarefa/etc.
 *   2026-07-24 v1.0.0 - Criação. 12 atalhos iniciais.
 * ============================================================================
 */
import type { LucideIcon } from 'lucide-react';
import {
  MessageSquarePlus,
  UserPlus,
  Briefcase,
  Users,
  FileSignature,
  Package,
  Receipt,
  Repeat,
  FileText,
  Handshake,
  Building2,
  UserCog,
  ListChecks,
  Megaphone,
  CalendarPlus,
  Calendar as CalendarIcon,
  CalendarClock,
  Zap,
  Workflow,
  Building,
  StickyNote,
} from 'lucide-react';
import type { ShortcutModule } from '@/config/appShortcuts';

/**
 * Definição de uma ação de "criar novo" exibida no popover do header.
 * Reaproveita as noções de módulo e menuKeys de AppShortcutDef para
 * uniformizar o gate de acesso.
 */
export interface QuickCreateDef {
  /** Identificador estável, também salvo em quick_create_shortcuts.favorites. */
  key: string;
  /** Rótulo curto exibido no tile ("Novo negócio"). */
  label: string;
  /** Ícone lucide. */
  icon: LucideIcon;
  /** Rota base para navegação. */
  route: string;
  /** Valor do query param `?new=` que a página vai consumir. */
  newParam: string;
  /** Módulo do tenant necessário (mesmo enum do App Launcher). */
  module: ShortcutModule;
  /** menuKeys — ao menos uma precisa estar liberada (admin bypassa). */
  menuKeys?: string[];
  /**
   * Se true, apenas root_admin / tenant_admin vê o atalho.
   * Usado para rotas que já usam `requireAdmin` no PermissionGuard
   * (ex.: /tenant/company).
   */
  requireAdmin?: boolean;
  /** Cor de destaque do tile (Tailwind text-*). */
  accent?: string;
  /**
   * v1.5.0 — Tecla de atalho (letra única) exibida no menu compacto do
   * botão "+" ao lado da busca global. Ativa somente com o menu aberto.
   */
  shortcutKey?: string;
  /**
   * v1.5.0 — Marca a ação como "principal": aparece no menu compacto do
   * botão "+" do header (lista curta estilo Pipedrive).
   */
  primary?: boolean;
  /**
   * v1.6.0 — Quando true, a ação abre o modal de criação SOBRE a tela atual
   * (via QuickCreateModalsContext), sem navegar para a página do módulo.
   * Evita perder o contexto de trabalho do usuário.
   */
  inline?: boolean;
  /**
   * v1.6.0 — Rótulo curto usado apenas no menu compacto do botão "+" do
   * header (ex.: "Evento" em vez de "Evento manual"). O popover completo
   * continua exibindo `label`.
   */
  shortLabel?: string;
}


/**
 * Lista canônica das ações. A ordem aqui é usada para a categoria
 * "Todos" e como fallback quando o usuário não tem favoritos.
 */
export const QUICK_CREATE_ACTIONS: QuickCreateDef[] = [
  // ---------------- Conversas / Contatos ----------------
  {
    key: 'qc_start_chat',
    label: 'Iniciar conversa',
    icon: MessageSquarePlus,
    // v1.1.0: aponta diretamente para a tela de chat; ConversationList
    // detecta ?new=1 e abre o mesmo StartNewConversationDialog do botão "+".
    route: '/tenant/chat',
    newParam: '1',
    module: 'conversations',
    menuKeys: ['chat'],
    accent: 'text-sky-600',
    primary: true,
    shortcutKey: 'I',
  },
  {
    key: 'qc_contact',
    label: 'Novo contato',
    icon: UserPlus,
    route: '/tenant/contacts',
    newParam: '1',
    module: 'shared',
    menuKeys: ['contacts'],
    accent: 'text-sky-600',
    primary: true,
    shortcutKey: 'C',
    inline: true,
  },
  {
    key: 'qc_contact_note',
    label: 'Nova anotação',
    shortLabel: 'Anotações',
    icon: StickyNote,
    // Rota de fallback: usada apenas se o provider de modais não estiver montado.
    route: '/tenant/contacts',
    newParam: 'note',
    module: 'shared',
    menuKeys: ['contacts'],
    accent: 'text-amber-600',
    primary: true,
    shortcutKey: 'A',
    inline: true,
  },
  {
    key: 'qc_template',
    label: 'Nova template',
    icon: FileText,
    route: '/tenant/templates',
    newParam: '1',
    module: 'shared',
    // v1.8.0: respeita a permissão dedicada da tela de Templates
    menuKeys: ['templates'],
    accent: 'text-sky-600',
  },

  // ---------------- Negócio (CRM) ----------------
  {
    key: 'qc_deal',
    label: 'Novo negócio',
    icon: Briefcase,
    route: '/tenant/crm/deals',
    newParam: '1',
    module: 'crm',
    menuKeys: ['crm_deals'],
    accent: 'text-emerald-600',
    primary: true,
    shortcutKey: 'N',
    inline: true,
  },
  {
    key: 'qc_customer',
    label: 'Novo cliente',
    icon: Users,
    route: '/tenant/crm/customers',
    newParam: '1',
    module: 'crm',
    menuKeys: ['crm_customers'],
    accent: 'text-emerald-600',
    primary: true,
    shortcutKey: 'L',
    inline: true,
  },
  {
    key: 'qc_contract',
    label: 'Novo contrato',
    icon: FileSignature,
    // v1.1.0: corrigido — o modal real (ContractWizardDialog) vive em
    // /tenant/crm/generated-contracts, não em /tenant/crm/contracts
    // (esta última é a página de templates publicados).
    route: '/tenant/crm/generated-contracts',
    newParam: '1',
    module: 'crm',
    menuKeys: ['crm_contracts'],
    accent: 'text-emerald-600',
  },
  {
    key: 'qc_product',
    label: 'Novo produto',
    icon: Package,
    route: '/tenant/crm/products',
    newParam: '1',
    module: 'crm',
    menuKeys: ['crm_products'],
    accent: 'text-emerald-600',
  },

  // ---------------- Financeiro ----------------
  {
    key: 'qc_charge',
    label: 'Nova cobrança',
    icon: Receipt,
    route: '/tenant/financial/charges',
    newParam: '1',
    module: 'financial',
    menuKeys: ['financial_charges'],
    accent: 'text-rose-600',
  },
  {
    key: 'qc_subscription',
    label: 'Nova recorrência',
    icon: Repeat,
    route: '/tenant/financial/subscriptions',
    newParam: '1',
    module: 'financial',
    menuKeys: ['financial_charges'],
    accent: 'text-rose-600',
  },

  // ---------------- Calendário (novo, v1.1.0) ----------------
  {
    key: 'qc_event_manual',
    label: 'Evento manual',
    icon: CalendarPlus,
    route: '/tenant/calendar',
    newParam: 'manual',
    module: 'shared',
    accent: 'text-amber-600',
    primary: true,
    shortcutKey: 'E',
    inline: true,
    shortLabel: 'Evento',
  },
  {
    key: 'qc_event_google',
    label: 'Evento Google',
    icon: CalendarIcon,
    route: '/tenant/calendar',
    newParam: 'google',
    module: 'shared',
    accent: 'text-amber-600',
  },
  {
    key: 'qc_booking',
    label: 'Agendamento online',
    icon: CalendarClock,
    route: '/tenant/calendar',
    newParam: 'booking',
    module: 'shared',
    menuKeys: ['scheduling'],
    accent: 'text-amber-600',
  },

  // ---------------- Produtividade (novo, v1.1.0) ----------------
  {
    key: 'qc_task',
    label: 'Nova tarefa',
    icon: ListChecks,
    route: '/tenant/tasks/list',
    newParam: '1',
    module: 'tasks',
    menuKeys: ['tasks_list'],
    accent: 'text-violet-600',
    primary: true,
    shortcutKey: 'T',
    inline: true,
  },
  {
    key: 'qc_campaign',
    label: 'Nova campanha',
    icon: Megaphone,
    route: '/tenant/campaigns',
    newParam: '1',
    module: 'shared',
    menuKeys: ['campaigns'],
    accent: 'text-violet-600',
  },

  // ---------------- Importação (v1.3.0) ----------------
  {
    key: 'qc_list_import_contacts',
    label: 'Lista Contatos',
    icon: ListChecks,
    route: '/tenant/lists?tab=contacts',
    newParam: '1',
    module: 'shared',
    menuKeys: ['lists_contacts'],
    accent: 'text-teal-600',
  },
  {
    key: 'qc_list_import_deals',
    label: 'Lista Negócios',
    icon: ListChecks,
    route: '/tenant/lists?tab=deals',
    newParam: '1',
    module: 'crm',
    menuKeys: ['lists_deals'],
    accent: 'text-teal-600',
  },
  {
    key: 'qc_list_import_customers',
    label: 'Lista Clientes',
    icon: ListChecks,
    route: '/tenant/lists?tab=customers',
    newParam: '1',
    module: 'crm',
    menuKeys: ['lists_customers'],
    accent: 'text-teal-600',
  },
  {
    key: 'qc_list_import_affiliates',
    label: 'Lista Afiliados',
    icon: ListChecks,
    route: '/tenant/lists?tab=affiliates',
    newParam: '1',
    module: 'referral',
    menuKeys: ['lists_affiliates'],
    accent: 'text-teal-600',
  },

  // ---------------- Atendimento / Automação (novo, v1.2.0) ----------------
  {
    key: 'qc_routing_rule',
    label: 'Nova ação rápida',
    icon: Zap,
    route: '/tenant/routing',
    newParam: '1',
    module: 'shared',
    menuKeys: ['routing_rules'],
    accent: 'text-sky-600',
  },
  {
    key: 'qc_flow',
    label: 'Novo fluxo',
    icon: Workflow,
    // Rota já é a de criação; o ?new=1 anexado é inofensivo.
    route: '/tenant/flows/new',
    newParam: '1',
    module: 'conversations',
    menuKeys: ['flows'],
    accent: 'text-sky-600',
  },
  {
    key: 'qc_department',
    label: 'Novo departamento',
    icon: Building,
    route: '/tenant/departments',
    newParam: '1',
    module: 'shared',
    menuKeys: ['departments'],
    accent: 'text-sky-600',
  },

  // ---------------- Indicação ----------------
  {
    key: 'qc_referral_program',
    label: 'Novo programa de indicação',
    icon: Handshake,
    route: '/tenant/indicacao/programas',
    newParam: '1',
    module: 'referral',
    menuKeys: ['referral_programs'],
    accent: 'text-fuchsia-600',
  },
  {
    key: 'qc_referral_campaign',
    label: 'Nova campanha de indicação',
    icon: Megaphone,
    route: '/tenant/indicacao/campanhas',
    newParam: '1',
    module: 'referral',
    menuKeys: ['referral_campaigns'],
    accent: 'text-fuchsia-600',
  },
  {
    key: 'qc_affiliate',
    label: 'Novo afiliado',
    icon: Handshake,
    route: '/tenant/indicacao/afiliados',
    newParam: '1',
    module: 'referral',
    menuKeys: ['referral_affiliates'],
    accent: 'text-fuchsia-600',
  },

  // ---------------- Configuração (v1.4.0: gate por permissão) ----------------
  {
    key: 'qc_company',
    label: 'Nova empresa',
    icon: Building2,
    route: '/tenant/company',
    newParam: '1',
    module: 'shared',
    // Rota /tenant/company usa PermissionGuard requireAdmin.
    requireAdmin: true,
    accent: 'text-slate-600',
  },
  {
    key: 'qc_agent',
    label: 'Novo usuário',
    icon: UserCog,
    route: '/tenant/agents',
    newParam: '1',
    module: 'shared',
    menuKeys: ['agents'],
    accent: 'text-slate-600',
  },
];

/** Lookup rápido por chave. */
export const QUICK_CREATE_BY_KEY: Record<string, QuickCreateDef> =
  QUICK_CREATE_ACTIONS.reduce(
    (acc, s) => {
      acc[s.key] = s;
      return acc;
    },
    {} as Record<string, QuickCreateDef>,
  );

/** Categorias exibidas em "Todas as ações". Igual ao padrão do App Launcher. */
export interface QuickCreateCategory {
  key: string;
  /** null = sem cabeçalho (reaproveita o título "Todas as ações"). */
  title: string | null;
  actionKeys: string[];
}

export const QUICK_CREATE_CATEGORIES: QuickCreateCategory[] = [
  {
    key: 'conversations',
    title: null,
    actionKeys: [
      'qc_start_chat',
      'qc_contact',
      'qc_template',
      'qc_routing_rule',
      'qc_flow',
      'qc_department',
    ],
  },
  {
    key: 'business',
    title: 'Negócio',
    actionKeys: ['qc_deal', 'qc_customer', 'qc_contract', 'qc_product'],
  },
  {
    key: 'financial',
    title: 'Financeiro',
    actionKeys: ['qc_charge', 'qc_subscription'],
  },
  {
    key: 'calendar',
    title: 'Calendário',
    actionKeys: ['qc_event_manual', 'qc_event_google', 'qc_booking'],
  },
  {
    key: 'productivity',
    title: 'Produtividade',
    actionKeys: ['qc_task', 'qc_campaign'],
  },
  {
    key: 'importacao',
    title: 'Importação',
    actionKeys: [
      'qc_list_import_contacts',
      'qc_list_import_deals',
      'qc_list_import_customers',
      'qc_list_import_affiliates',
    ],
  },
  {
    key: 'referral',
    title: 'Indicação',
    actionKeys: ['qc_referral_program', 'qc_referral_campaign', 'qc_affiliate'],
  },
  {
    key: 'settings',
    title: 'Configuração',
    actionKeys: ['qc_company', 'qc_agent'],
  },
];
